import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiArrowLeft, FiUsers, FiCalendar, FiMapPin, FiCopy, FiCheck, FiLogOut, FiShield, FiUserX, FiUserCheck, FiUserPlus, FiZap, FiEdit3, FiX, FiFlag, FiChevronRight, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import EditGroupModal from '../components/EditGroupModal';
import { leaveGroup, deleteGroup, removeMemberFromGroup, promoteMemberToAdmin } from '../services/groupService';
import { sendFriendRequest, cancelFriendRequest, getFriendshipWith } from '../services/friendService';
import { useAuth } from '../contexts/AuthContext';
import AvatarBadge from '../components/AvatarBadge';
import { formatClubLocation } from '../utils/formatClub';
import { formatClubDisplayName } from '../utils/clubDisplay';

function GroupDetails({ groups, onGroupDeleted }) {
  const { id } = useParams();
  const navigate = useNavigate();
  // Support invite_code URLs and numeric ID URLs (backwards compat)
  const group = groups.find(g => {
    // Try invite_code match first (new URL format)
    if (g.invite_code?.toUpperCase() === id?.toUpperCase()) return true;
    // Fall back to numeric ID match (backwards compatibility)
    if (!isNaN(id) && g.id === parseInt(id)) return true;
    return false;
  });

  const [inviteCodeCopied, setInviteCodeCopied] = useState(false);
  const [showInviteSection, setShowInviteSection] = useState(false);
  const [loading, setLoading] = useState(false);

  // Group editing state
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);

  // Member actions modal state
  const [showMemberActionsModal, setShowMemberActionsModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [friendship, setFriendship] = useState(null);
  const [friendshipLoading, setFriendshipLoading] = useState(false);
  const [friendActionLoading, setFriendActionLoading] = useState(false);

  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;

  // Fetch friendship state whenever the modal opens for a new member.
  useEffect(() => {
    if (!showMemberActionsModal || !selectedMember?.userId) {
      setFriendship(null);
      return;
    }
    let cancelled = false;
    setFriendshipLoading(true);
    getFriendshipWith(selectedMember.userId)
      .then(({ data }) => {
        if (!cancelled) setFriendship(data);
      })
      .finally(() => {
        if (!cancelled) setFriendshipLoading(false);
      });
    return () => { cancelled = true; };
  }, [showMemberActionsModal, selectedMember?.userId]);

  const handleSendFriendRequest = async () => {
    if (!selectedMember?.userId) return;
    setFriendActionLoading(true);
    try {
      const { autoAccepted, error } = await sendFriendRequest(selectedMember.userId);
      if (error) {
        alert(error.message || 'Could not send friend request.');
      } else if (autoAccepted) {
        setShowMemberActionsModal(false);
      } else {
        const { data } = await getFriendshipWith(selectedMember.userId);
        setFriendship(data);
      }
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleCancelFriendRequest = async () => {
    if (!friendship?.friendshipId) return;
    setFriendActionLoading(true);
    try {
      const { error } = await cancelFriendRequest(friendship.friendshipId);
      if (error) {
        alert(error.message || 'Could not cancel request.');
      } else {
        setFriendship(null);
      }
    } finally {
      setFriendActionLoading(false);
    }
  };

  // Group editing handlers
  const handleEditGroup = () => {
    setShowEditGroupModal(true);
  };

  const handleGroupUpdateSuccess = async () => {
    // Refresh the page to get updated group data
    window.location.reload();
  };

  if (!group) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Helmet>
          <title>Group Not Found - TeeSync</title>
        </Helmet>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-text-primary">Group not found</h1>
          <button
            onClick={() => navigate('/')}
            className="mt-4 text-accent hover:opacity-80"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const copyInviteCode = async () => {
    if (group.invite_code) {
      try {
        await navigator.clipboard.writeText(group.invite_code);
        setInviteCodeCopied(true);
        setTimeout(() => setInviteCodeCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy invite code:', err);
      }
    }
  };

  const handleLeaveGroup = async () => {
    if (window.confirm('Are you sure you want to leave this group?')) {
      setLoading(true);
      const { error } = await leaveGroup(group.id);
      if (error) {
        alert('Failed to leave group: ' + error.message);
      } else {
        navigate('/');
      }
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    setLoading(true);
    const { data, error } = await deleteGroup(group.id);
    if (error) {
      alert('Failed to delete group: ' + error.message);
    } else {
      alert(`Group "${data.groupName}" deleted successfully!`);
      if (onGroupDeleted) onGroupDeleted();
      navigate('/');
    }
    setLoading(false);
  };

  const handleRemoveMember = async (userId, memberName) => {
    if (window.confirm(`Are you sure you want to remove ${memberName} from the group?`)) {
      setLoading(true);
      const { error } = await removeMemberFromGroup(group.id, userId);
      if (error) {
        alert('Failed to remove member: ' + error.message);
      } else {
        alert(`${memberName} has been removed from the group.`);
        // Refresh the page or update state
        window.location.reload();
      }
      setLoading(false);
    }
  };

  const handlePromoteMember = async (userId, memberName) => {
    if (window.confirm(`Are you sure you want to promote ${memberName} to admin?`)) {
      setLoading(true);
      const { error } = await promoteMemberToAdmin(group.id, userId);
      if (error) {
        alert('Failed to promote member: ' + error.message);
      } else {
        alert(`${memberName} has been promoted to admin.`);
        // Refresh the page or update state
        window.location.reload();
      }
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner':
        return { text: 'Owner', className: 'bg-accent/10 text-accent border-accent', icon: FiShield };
      case 'admin':
        return { text: 'Admin', className: 'bg-blue-100 text-blue-800 border-blue-200', icon: FiShield };
      default:
        return { text: 'Member', className: 'bg-surface text-text-primary border-border-subtle', icon: FiUsers };
    }
  };

  return (
    <div className="min-h-screen bg-app">
      <Helmet>
        <title>{group?.name || 'Group'} - TeeSync</title>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-accent hover:text-accent mb-6 group transition-all duration-200"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
              <span className="font-semibold">Back</span>
            </button>
          </div>

          {/* Title row */}
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-2xl lg:text-3xl font-bold text-text-primary truncate">
              {group.name}
            </h1>
            {(group.userRole === 'owner' || group.userRole === 'admin') && (
              <button
                onClick={handleEditGroup}
                className="w-10 h-10 bg-surface hover:bg-card rounded-lg flex items-center justify-center transition-colors duration-200 ml-3 flex-shrink-0"
                title="Edit group"
              >
                <SafeIcon icon={FiEdit3} className="w-4 h-4 text-text-secondary" />
              </button>
            )}
          </div>

          {/* Metadata strip - small inline pills */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 text-sm text-text-secondary">
            <span className="inline-flex items-center">
              <SafeIcon icon={FiUsers} className="w-4 h-4 mr-1.5 text-text-muted" />
              {group.memberCount} {group.memberCount === 1 ? 'member' : 'members'}
            </span>
            <span className="inline-flex items-center">
              <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1.5 text-text-muted" />
              {group.upcomingRounds || 0} upcoming
            </span>
            <span className="inline-flex items-center">
              <SafeIcon icon={FiFlag} className="w-4 h-4 mr-1.5 text-text-muted" />
              {group.roundsPlayed || 0} rounds played
            </span>
            {group.home_course && (
              <span className="inline-flex items-center">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 mr-1.5 text-text-muted" />
                {formatClubDisplayName(group.home_course)}
                {formatClubLocation(group.home_course) && (
                  <span className="text-text-muted">, {formatClubLocation(group.home_course)}</span>
                )}
              </span>
            )}
          </div>

          {/* Action bar */}
          <div className="flex flex-row items-center sm:justify-end gap-3 mb-6">
            <Link
              to={`/create-round/${group.id}`}
              className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center px-5 py-2.5 bg-accent text-white rounded-md hover:bg-interactive-hover transition-colors font-medium text-sm"
            >
              <SafeIcon icon={FiZap} className="w-4 h-4 mr-2" />
              New Round
            </Link>
            {group.isOwner && (
              <button
                onClick={() => setShowInviteSection(!showInviteSection)}
                className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center px-5 py-2.5 border border-border-subtle text-text-secondary rounded-md hover:bg-surface transition-colors font-medium text-sm"
              >
                <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                Invite
              </button>
            )}
            {!group.isOwner && (
              <button
                onClick={handleLeaveGroup}
                disabled={loading}
                className="flex-1 sm:flex-none min-h-[44px] inline-flex items-center justify-center px-5 py-2.5 border border-status-error/30 text-status-error rounded-md hover:bg-status-error/10 disabled:opacity-50 transition-colors font-medium text-sm"
              >
                <SafeIcon icon={FiLogOut} className="w-4 h-4 mr-2" />
                Leave Group
              </button>
            )}
          </div>

          {/* Collapsible Invite Section */}
          <AnimatePresence>
            {group.isOwner && showInviteSection && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-8 overflow-hidden"
              >
                <div className="p-5 bg-card border border-border-subtle rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold text-text-primary text-base">Invite to Group</h3>
                      <p className="text-text-secondary text-sm">
                        Share the code with friends
                      </p>
                    </div>
                    <button
                      onClick={() => setShowInviteSection(false)}
                      className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center hover:bg-card transition-colors duration-200"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
                    </button>
                  </div>

                  {/* Invite Code */}
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-text-secondary mb-2">
                      Invite Code
                    </label>
                    <div className="flex items-center gap-2 bg-surface rounded-lg p-3 border border-border-subtle">
                      <code className="flex-1 font-mono text-lg font-bold text-text-primary">
                        {group.invite_code}
                      </code>
                      <button
                        onClick={copyInviteCode}
                        className="inline-flex items-center px-4 py-2 bg-accent text-white rounded-md hover:bg-interactive-hover text-sm font-medium transition-colors whitespace-nowrap"
                      >
                        <SafeIcon icon={inviteCodeCopied ? FiCheck : FiCopy} className="w-4 h-4 mr-2" />
                        {inviteCodeCopied ? 'Copied!' : 'Copy code'}
                      </button>
                    </div>
                  </div>

                  <p className="text-text-muted text-xs">
                    Share the invite code with your friends. They enter it in "Join Group" to join.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Members section */}
          <div className="mb-10">
            <h2 className="text-xl font-semibold text-text-primary mb-4">Members</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[...group.members]
                .sort((a, b) => {
                  const nameA = (typeof a === 'string' ? a : a.name) || '';
                  const nameB = (typeof b === 'string' ? b : b.name) || '';
                  const bucket = (m, name) => {
                    if (m.role === 'owner') return 0;
                    if (currentUserId && m.userId === currentUserId) return 1;
                    return 2;
                  };
                  const ba = bucket(a, nameA);
                  const bb = bucket(b, nameB);
                  if (ba !== bb) return ba - bb;
                  return nameA.localeCompare(nameB);
                })
                .map((member, index) => {
                const roleBadge = getRoleBadge(member.role);
                const canManageMember = group.isOwner && member.role !== 'owner';
                const memberName = typeof member === 'string' ? member : member.name;
                const isSelf = currentUserId && member.userId === currentUserId;
                const isOwnerRow = member.role === 'owner';
                const isClickable = !isSelf;

                const joinedLabel = member.joinedAt
                  ? `Joined ${new Date(member.joinedAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`
                  : null;

                return (
                  <div
                    key={index}
                    className={`bg-card border border-border-subtle rounded-lg p-4 flex items-center justify-between ${
                      isClickable ? 'cursor-pointer hover:bg-surface transition-colors' : ''
                    }`}
                    onClick={() => {
                      if (isClickable) {
                        setSelectedMember(member);
                        setShowMemberActionsModal(true);
                      }
                    }}
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      <AvatarBadge
                        name={memberName}
                        size="md"
                        owner={isOwnerRow}
                        className="mr-3"
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-text-primary truncate">{memberName}</p>
                        <div className={`flex items-center mt-0.5 ${
                          member.role === 'owner' ? 'text-accent' :
                          member.role === 'admin' ? 'text-status-warning' :
                          'text-text-muted'
                        }`}>
                          <SafeIcon icon={roleBadge.icon} className="w-3 h-3 mr-1" />
                          <span className="text-xs font-medium">{roleBadge.text}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center flex-shrink-0 ml-2 gap-2">
                      {joinedLabel && (
                        <span className="text-xs text-text-muted whitespace-nowrap">{joinedLabel}</span>
                      )}
                      {isClickable && (
                        <SafeIcon icon={FiChevronRight} className="w-4 h-4 text-text-muted" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </motion.div>


        {/* Edit Group Modal */}
        <EditGroupModal
          isOpen={showEditGroupModal}
          onClose={() => setShowEditGroupModal(false)}
          group={group}
          onUpdateSuccess={handleGroupUpdateSuccess}
          onDeleteGroup={handleDeleteGroup}
        />

        {/* Member Actions Modal */}
        {showMemberActionsModal && selectedMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end lg:items-center justify-center p-4 pb-20 lg:pb-4 z-50">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 100 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 100 }}
              className="bg-card rounded-t-3xl lg:rounded-lg  w-full max-w-md mb-0 lg:mb-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-border-subtle">
                <div className="flex items-center">
                  <AvatarBadge name={selectedMember.name} size="lg" className="mr-3" />
                  <div>
                    <h3 className="text-lg font-bold text-text-primary">{selectedMember.name}</h3>
                    <p className="text-sm text-text-secondary flex items-center">
                      <SafeIcon icon={getRoleBadge(selectedMember.role).icon} className="w-3 h-3 mr-1" />
                      {getRoleBadge(selectedMember.role).text}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMemberActionsModal(false)}
                  className="w-10 h-10 bg-surface rounded-lg flex items-center justify-center hover:bg-card transition-colors duration-200"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5 text-text-secondary" />
                </button>
              </div>

              {/* Actions */}
              <div className="p-6 space-y-3">
                {(() => {
                  const canManageThisMember = group.isOwner && selectedMember.role !== 'owner';
                  const renderFriendButton = () => {
                    if (friendshipLoading) {
                      return (
                        <button
                          disabled
                          className="w-full flex items-center px-4 py-3 bg-surface text-text-muted rounded-lg font-medium"
                        >
                          <SafeIcon icon={FiLoader} className="w-5 h-5 mr-3 animate-spin" />
                          Checking…
                        </button>
                      );
                    }
                    if (!friendship) {
                      return (
                        <button
                          onClick={handleSendFriendRequest}
                          disabled={friendActionLoading}
                          className="w-full flex items-center px-4 py-3 bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors font-medium disabled:opacity-50"
                        >
                          <SafeIcon
                            icon={friendActionLoading ? FiLoader : FiUserPlus}
                            className={`w-5 h-5 mr-3 ${friendActionLoading ? 'animate-spin' : ''}`}
                          />
                          Send Friend Request
                        </button>
                      );
                    }
                    if (friendship.status === 'pending' && friendship.direction === 'outgoing') {
                      return (
                        <button
                          onClick={handleCancelFriendRequest}
                          disabled={friendActionLoading}
                          className="w-full flex items-center px-4 py-3 bg-surface text-text-secondary rounded-lg hover:bg-card transition-colors font-medium disabled:opacity-50"
                        >
                          <SafeIcon
                            icon={friendActionLoading ? FiLoader : FiX}
                            className={`w-5 h-5 mr-3 ${friendActionLoading ? 'animate-spin' : ''}`}
                          />
                          Cancel Friend Request
                        </button>
                      );
                    }
                    if (friendship.status === 'pending' && friendship.direction === 'incoming') {
                      return (
                        <button
                          disabled
                          className="w-full flex items-center px-4 py-3 bg-surface text-text-muted rounded-lg font-medium opacity-75 cursor-not-allowed"
                        >
                          <SafeIcon icon={FiUserPlus} className="w-5 h-5 mr-3" />
                          Respond on Friends page
                        </button>
                      );
                    }
                    if (friendship.status === 'accepted') {
                      return (
                        <button
                          disabled
                          className="w-full flex items-center px-4 py-3 bg-accent/10 text-accent rounded-lg font-medium opacity-75 cursor-not-allowed"
                        >
                          <SafeIcon icon={FiCheck} className="w-5 h-5 mr-3" />
                          Already Friends
                        </button>
                      );
                    }
                    return null;
                  };

                  return (
                    <>
                      {renderFriendButton()}
                      {canManageThisMember && selectedMember.role === 'member' && (
                        <button
                          onClick={() => {
                            handlePromoteMember(selectedMember.userId, selectedMember.name);
                            setShowMemberActionsModal(false);
                          }}
                          className="w-full flex items-center px-4 py-3 bg-accent/10 text-accent rounded-lg hover:bg-accent/20 transition-colors font-medium"
                        >
                          <SafeIcon icon={FiUserCheck} className="w-5 h-5 mr-3" />
                          Promote to Admin
                        </button>
                      )}
                      {canManageThisMember && (
                        <button
                          onClick={() => {
                            handleRemoveMember(selectedMember.userId, selectedMember.name);
                            setShowMemberActionsModal(false);
                          }}
                          className="w-full flex items-center px-4 py-3 bg-status-error/10 text-status-error rounded-lg hover:bg-status-error/20 transition-colors font-medium"
                        >
                          <SafeIcon icon={FiUserX} className="w-5 h-5 mr-3" />
                          Remove from Group
                        </button>
                      )}
                      <button
                        onClick={() => setShowMemberActionsModal(false)}
                        className="w-full flex items-center justify-center px-4 py-3 bg-surface text-text-secondary rounded-lg hover:bg-card transition-colors font-medium"
                      >
                        Cancel
                      </button>
                    </>
                  );
                })()}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

export default GroupDetails;